import { ReactElement } from "react";
import styled from "styled-components";
import MobileAppDownloadCtas from "components/MobileAppDownloadCtas";
import JobsSearchRow from "./JobsSearchRow";
import FeaturedJobsContainer from "./FeaturedJobsContainer";
import { JobDetails } from "core/jobs/types";
import { CurrentlySelectedCard } from "./JobsHome";
import * as gtag from "lib/analytics/gtag";
import {
  AnalyticsEvents,
  InternalAnalyticsEvents,
} from "lib/analytics/constants";
import { track } from "lib/analytics/analytics";
import { useTracking } from "core/tracking/useTracking";
import responsiveStyling, { responsiveStylingMax } from "lib/responsive";
import {
  BrandColorPalette,
  PrimaryColorPalette,
  StatusColorPalette,
} from "lib/styles/colors";

const EMPLOYER_CTA_HREF = "https://www.10ure.com/post-a-job";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  padding-top: 40px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  ${responsiveStylingMax({
    "<1024": `
    max-width: 100%;
    `,
  })};
`;

const PromoRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  gap: 24px;
  margin-bottom: 32px;

  > * {
    min-width: 0;
  }

  ${responsiveStyling({
    ">768": `
      flex-direction: row;
      align-items: stretch;

      > * {
        flex: 1 1 0;
        width: auto;
      }
    `,
  })};
`;

const EmployerCta = styled.section`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin-top: 0;
  margin-bottom: 0;
  padding: 24px 20px;
  background-color: ${BrandColorPalette.DECORATION_TWO};
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(24, 32, 217, 0.2);
  ${responsiveStyling({
    ">1024": `
      padding: 28px 32px;
    `,
  })};
`;

const EmployerCtaCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 16px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const EmployerCtaHeadline = styled.h2`
  margin: 0;
  color: ${PrimaryColorPalette.MAIN_6};
  font-family: Gilroy;
  font-weight: 900;
  font-size: 22px;
  line-height: 28px;
  letter-spacing: 0.2px;
  overflow-wrap: break-word;
  text-align: left;
  ${responsiveStyling({
    ">1024": `
      font-size: 28px;
      line-height: 34px;
    `,
  })};
`;

const EmployerCtaSubtext = styled.p`
  margin: 8px 0 0;
  color: ${BrandColorPalette.BASIC_DECORATION_BLUE};
  font-family: SFProDisplay;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  overflow-wrap: break-word;
  text-align: left;
`;

const EmployerCtaLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  padding: 14px 24px;
  border-radius: 8px;
  background-color: ${PrimaryColorPalette.MAIN_6};
  color: ${BrandColorPalette.DECORATION_TWO};
  font-family: SFProDisplay;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.2px;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background-color: ${BrandColorPalette.BASIC_DECORATION_BLUE};
    color: ${BrandColorPalette.DECORATION_TWO};
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:focus {
    outline: 3px solid ${StatusColorPalette.WARNING_DECORATION};
    outline-offset: 3px;
  }

  ${responsiveStyling({
    ">768": `
      width: auto;
      align-self: flex-start;
      flex-shrink: 0;
      margin-top: auto;
      padding: 14px 28px;
    `,
  })};
`;

const VisuallyHidden = styled.span`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

interface Props {
  featuredJobs: JobDetails[];
  handleSelectJob: (newSelection: JobDetails) => void;
  handleDeselect: () => void;
  userRefId: string;
  isMobile: boolean;
  currentlySelectedCard: CurrentlySelectedCard;
}
export default function JobsHeader({
  featuredJobs,
  handleSelectJob,
  handleDeselect,
  userRefId,
  isMobile,
  currentlySelectedCard,
}: Props): ReactElement {
  const { internalTrackEvent } = useTracking();

  const handleEmployerClick = () => {
    gtag.event({
      action: "click_employer_looking_button",
      category: "engagement",
    });
    gtag.trackEmployerCtaClick();
    internalTrackEvent(InternalAnalyticsEvents.CLICK_EMPLOYER_LOOKING);
    track(AnalyticsEvents.CLICK_EMPLOYER_LOOKING);
  };

  return (
    <Container>
      <PromoRow>
        <MobileAppDownloadCtas split />
        <EmployerCta>
          <EmployerCtaCopy>
            <EmployerCtaHeadline>
              Hiring mariners? Post your jobs for free.
            </EmployerCtaHeadline>
            <EmployerCtaSubtext>
              Reach qualified mariners actively looking for work on 10ure.
            </EmployerCtaSubtext>
          </EmployerCtaCopy>
          <EmployerCtaLink
            href={EMPLOYER_CTA_HREF}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleEmployerClick}
          >
            Post a job free
            <VisuallyHidden> (opens in a new tab)</VisuallyHidden>
          </EmployerCtaLink>
        </EmployerCta>
      </PromoRow>
      <JobsSearchRow />
      <FeaturedJobsContainer
        featuredJobs={featuredJobs ?? []}
        userRefId={userRefId}
        isMobile={isMobile ?? false}
        handleSelectJob={handleSelectJob}
        handleDeselect={handleDeselect}
        currentlySelectedCard={currentlySelectedCard}
      />
    </Container>
  );
}
